import { useMemo, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  DataTable,
  EstadoVazio,
  Pill,
  SelectField,
  Tabs,
  TextField,
  TextareaField,
  type Column,
  fmtBRL,
} from "@atlas/ui/web";
import {
  ApiHttpError,
  type ArquivoConsignataria,
  type ArquivoPendenteConsignataria,
  type BloqueioArquivoConsignataria,
  type DeclaracoesArquivoConsignataria,
  type LinhaArquivoConsignataria,
  type LinhaResultadoConsignataria,
  type PreviaArquivoConsignataria,
  type SituacaoLinhaArquivoConsignataria,
} from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { ConsignacoesDoCredor } from "./consignatarias-consignacoes";
import { RepassesPainel } from "../../components/RepassesPainel";
import { RepassePorCredorFinal } from "./repasse-credor-final";

// O ARQUIVO MENSAL DA CONSIGNATÁRIA (#198, Fase 3.4 da #165).
//
// A averbadora sobe o arquivo em nome da consignatária (decidido em
// 25/09/2026). A tela é a prévia do que a confirmação faria — por rubrica,
// quem sai por omissão, os bloqueios que pedem declaração e os alertas — e a
// confirmação, que só grava o que a prévia mostrou (`previaHash`).

const reais = (centavos: number) => fmtBRL(centavos / 100);

const SITUACAO_ROTULO: Record<SituacaoLinhaArquivoConsignataria, string> = {
  aceita: "Aceita",
  recusada: "Recusada",
  conferencia: "Conferência (crédito)",
  descartada: "Descartada",
  aguardando_cadastro: "Aguardando cadastro",
};

const SITUACAO_PILL = {
  aceita: "aceita",
  recusada: "rejeitada",
  conferencia: "emdia",
  descartada: "expirado",
  aguardando_cadastro: "pendente",
} as const;

const ESTADO_ROTULO: Record<ArquivoConsignataria["estado"], string> = {
  previa: "Em prévia",
  confirmado: "Confirmado",
  substituido: "Substituído",
};

const RESULTADO_ROTULO: Record<LinhaResultadoConsignataria["resultado"], string> = {
  descontado: "Descontado",
  parcial: "Parcial",
  recusado: "Recusado",
  ausente: "Ausente da folha",
};

const RESULTADO_PILL = { descontado: "aceita", parcial: "pendente", recusado: "rejeitada", ausente: "expirado" } as const;

/** Salva um Blob como arquivo, pelo link temporário. */
function salvar(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

/** A declaração que cada bloqueio pede, digitada na tela. */
type Declaracao = { justificativa: string; quantidade: string };

function chaveDoBloqueio(b: BloqueioArquivoConsignataria): string {
  return b.tipo === "corte_passou" ? "corte" : `${b.tipo}:${b.rubrica}`;
}

/** As declarações que o pedido leva, montadas do que foi digitado. */
function declaracoesDe(bloqueios: readonly BloqueioArquivoConsignataria[], digitado: Record<string, Declaracao>): DeclaracoesArquivoConsignataria {
  const rubricasAusentes: NonNullable<DeclaracoesArquivoConsignataria["rubricasAusentes"]> = [];
  const encerramentos: NonNullable<DeclaracoesArquivoConsignataria["encerramentos"]> = [];
  for (const b of bloqueios) {
    const d = digitado[chaveDoBloqueio(b)];
    if (!d || b.tipo === "corte_passou") continue;
    if (b.tipo === "rubrica_ausente") rubricasAusentes.push({ rubrica: b.rubrica, justificativa: d.justificativa.trim() });
    if (b.tipo === "queda_grande") {
      encerramentos.push({ rubrica: b.rubrica, quantidade: Number(d.quantidade), justificativa: d.justificativa.trim() });
    }
  }
  return {
    ...(rubricasAusentes.length ? { rubricasAusentes } : {}),
    ...(encerramentos.length ? { encerramentos } : {}),
  };
}

/** O bloqueio está declarado como a API exige: justificativa, e na queda grande a quantidade exata. */
function declarado(b: BloqueioArquivoConsignataria, d: Declaracao | undefined): boolean {
  if (b.tipo === "corte_passou") return false;
  if (!d || d.justificativa.trim() === "") return false;
  return b.tipo === "rubrica_ausente" || Number(d.quantidade) === b.encerramentos;
}

export function AdminConsignatariasArquivos() {
  const qc = useQueryClient();
  const [aba, setAba] = useState<"fila" | "arquivos" | "consignacoes">("fila");
  const [credorId, setCredorId] = useState<number | null>(null);
  const [unidadeId, setUnidadeId] = useState<number | null>(null);
  const [competencia, setCompetencia] = useState("");
  const [arquivoId, setArquivoId] = useState<string | null>(null);
  const [digitado, setDigitado] = useState<Record<string, Declaracao>>({});
  const [situacao, setSituacao] = useState<SituacaoLinhaArquivoConsignataria | "">("");
  const [aviso, setAviso] = useState<string | null>(null);

  const bancosQ = useQuery({ queryKey: ["admin", "bancos", "consignatarias"], queryFn: () => atlas.admin.listBancos() });
  const orgaosQ = useQuery({ queryKey: ["admin", "prefeituras", "consignatarias"], queryFn: () => atlas.admin.listPrefeituras() });

  // A fila de quem espera confirmação, de todos os bancos e órgãos: sem ela o
  // arquivo que o banco enviou só aparece para quem escolhe o par certo.
  const pendentesQ = useQuery({
    queryKey: ["admin", "consignatarias", "pendentes"],
    queryFn: () => atlas.admin.consignatarias.arquivosPendentes(),
  });

  const arquivosQ = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "arquivos", unidadeId, competencia],
    queryFn: () => atlas.admin.consignatarias.listarArquivos(credorId!, {
      unidadeId: unidadeId!, ...(/^\d{6}$/.test(competencia) ? { competencia } : {}),
    }),
    enabled: credorId !== null && unidadeId !== null,
  });

  const previaQ = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "previa", arquivoId],
    queryFn: () => atlas.admin.consignatarias.previa(credorId!, arquivoId!),
    enabled: credorId !== null && arquivoId !== null,
  });

  const linhasQ = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "linhas", arquivoId, situacao],
    queryFn: () => atlas.admin.consignatarias.linhas(credorId!, arquivoId!, situacao ? { situacao: [situacao] } : {}),
    enabled: credorId !== null && arquivoId !== null,
  });

  // O RESULTADO (#201): depois do retorno, o que a folha fez com cada linha. Só
  // existe para o arquivo confirmado da competência.
  const confirmadoAberto = previaQ.data?.arquivo.estado === "confirmado" ? previaQ.data.arquivo : null;
  const resultadoQ = useQuery({
    queryKey: ["admin", "consignatarias", credorId, "resultado", confirmadoAberto?.unidadeId, confirmadoAberto?.competencia],
    queryFn: () => atlas.admin.consignatarias.resultado(credorId!, {
      unidadeId: confirmadoAberto!.unidadeId, competencia: confirmadoAberto!.competencia,
    }),
    enabled: credorId !== null && confirmadoAberto !== null,
    retry: false,
  });

  const enviar = useMutation({
    mutationFn: (file: File) => atlas.admin.consignatarias.enviarArquivo(credorId!, unidadeId!, file, file.name),
    onSuccess: (r) => {
      // Credor com a confirmação automática ligada: o envio já confirmou (ou diz por que não).
      const auto = r.confirmacaoAutomatica;
      setAviso(
        auto === null || auto === undefined
          ? null
          : auto.confirmada
            ? "Arquivo confirmado automaticamente: a confirmação automática desta consignatária está ligada. Os valores entram no movimento da competência."
            : `A confirmação automática não confirmou o arquivo: ${auto.motivo} Ele segue em prévia, aguardando a confirmação.`,
      );
      setDigitado({});
      setArquivoId(r.arquivo.id);
      qc.setQueryData(["admin", "consignatarias", credorId, "previa", r.arquivo.id], r);
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", credorId, "arquivos"] });
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", "pendentes"] });
    },
  });

  const confirmar = useMutation({
    mutationFn: (previa: PreviaArquivoConsignataria) => atlas.admin.consignatarias.confirmar(credorId!, previa.arquivoId, {
      previaHash: previa.previaHash,
      declaracoes: declaracoesDe(previa.bloqueios, digitado),
    }),
    onSuccess: (r) => {
      setAviso(
        r.resultado.repetido
          ? "Este arquivo já estava confirmado: nada foi gravado de novo."
          : r.resultado.movimentoDesatualizado
            ? "Arquivo confirmado. O movimento desta competência já tinha sido emitido: retifique-o para a folha receber os valores novos."
            : "Arquivo confirmado. Os valores entram no movimento da competência.",
      );
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", credorId] });
      void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", "pendentes"] });
    },
  });

  const previa = previaQ.data?.previa ?? null;
  const arquivo = previaQ.data?.arquivo ?? null;
  const cortePassou = previa?.bloqueios.some((b) => b.tipo === "corte_passou") ?? false;
  const faltaDeclarar = previa?.bloqueios.filter((b) => b.tipo !== "corte_passou" && !declarado(b, digitado[chaveDoBloqueio(b)])) ?? [];
  const podeConfirmar = !!previa && arquivo?.estado === "previa" && !cortePassou && faltaDeclarar.length === 0 && !confirmar.isPending;

  const opcoesCredor = useMemo(
    () => (bancosQ.data?.bancos ?? []).map((b) => ({ value: String(b.id), label: b.nome })),
    [bancosQ.data],
  );
  const opcoesOrgao = useMemo(
    () => (orgaosQ.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: p.nome })),
    [orgaosQ.data],
  );

  const aoEscolherArquivo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) enviar.mutate(file);
    e.target.value = "";
  };

  const baixarCsv = async () => {
    if (credorId === null || !arquivoId) return;
    const blob = await atlas.admin.consignatarias.linhasCsv(credorId, arquivoId, situacao ? [situacao] : []);
    salvar(blob, `${(arquivo?.nome ?? "arquivo").replace(/\.txt$/i, "")}${situacao ? `-${situacao}` : ""}.csv`);
  };

  const baixarResultadoCsv = async () => {
    if (credorId === null || !confirmadoAberto) return;
    const blob = await atlas.admin.consignatarias.resultadoCsv(credorId, {
      unidadeId: confirmadoAberto.unidadeId, competencia: confirmadoAberto.competencia,
    });
    salvar(blob, `resultado-${confirmadoAberto.nome.replace(/\.txt$/i, "")}.csv`);
  };

  const colunasResultado: Column<LinhaResultadoConsignataria>[] = [
    { key: "linha", header: "Linha", align: "right", render: (l) => l.linha },
    { key: "matricula", header: "Matrícula", mono: true, render: (l) => l.matricula },
    { key: "rubrica", header: "Rubrica", mono: true, render: (l) => l.rubrica },
    { key: "informado", header: "Informado", align: "right", render: (l) => reais(l.informadoCentavos) },
    { key: "pago", header: "Descontado", align: "right", render: (l) => (l.pagoCentavos === null ? "—" : reais(l.pagoCentavos)) },
    { key: "resultado", header: "Resultado", render: (l) => <Pill variant={RESULTADO_PILL[l.resultado]}>{RESULTADO_ROTULO[l.resultado]}</Pill> },
    { key: "motivo", header: "Motivo da folha", render: (l) => l.motivo ?? "" },
  ];

  const colunasLinhas: Column<LinhaArquivoConsignataria>[] = [
    { key: "linha", header: "Linha", align: "right", render: (l) => l.linha },
    { key: "matricula", header: "Matrícula", mono: true, render: (l) => l.matricula },
    { key: "ff", header: "FF", align: "right", render: (l) => l.ff },
    { key: "rubrica", header: "Rubrica", mono: true, render: (l) => l.rubrica },
    { key: "valor", header: "Valor", align: "right", render: (l) => reais(l.valorCentavos) },
    { key: "situacao", header: "Situação", render: (l) => <Pill variant={SITUACAO_PILL[l.situacao]}>{SITUACAO_ROTULO[l.situacao]}</Pill> },
    { key: "motivo", header: "Motivo", render: (l) => l.motivo ?? "" },
  ];

  const abrirPendente = (a: ArquivoPendenteConsignataria) => {
    setAba("arquivos"); // a prévia e a confirmação moram na aba de arquivos
    setCredorId(a.credorId);
    setUnidadeId(a.unidadeId);
    setArquivoId(a.id);
    setDigitado({});
    setAviso(null);
  };

  const colunasPendentes: Column<ArquivoPendenteConsignataria>[] = [
    { key: "consignataria", header: "Consignatária", wrap: true, render: (a) => a.consignataria },
    { key: "orgao", header: "Órgão/entidade", wrap: true, render: (a) => a.orgao },
    { key: "competencia", header: "Competência", mono: true, render: (a) => a.competencia },
    { key: "nome", header: "Arquivo", render: (a) => a.nome },
    { key: "linhas", header: "Linhas", align: "right", render: (a) => a.linhas },
    { key: "total", header: "Total aceito", align: "right", render: (a) => reais(a.totalCentavos) },
    { key: "enviado", header: "Enviado por", render: (a) => `${a.enviadoPor} em ${a.criadoEm.slice(0, 16).replace("T", " ")}` },
  ];

  const colunasArquivos: Column<ArquivoConsignataria>[] = [
    { key: "competencia", header: "Competência", mono: true, render: (a) => a.competencia },
    { key: "nome", header: "Arquivo", render: (a) => a.nome },
    { key: "estado", header: "Situação", render: (a) => ESTADO_ROTULO[a.estado] },
    { key: "linhas", header: "Linhas", align: "right", render: (a) => a.linhas },
    { key: "total", header: "Total aceito", align: "right", render: (a) => reais(a.totalCentavos) },
    { key: "enviado", header: "Enviado por", render: (a) => `${a.enviadoPor} em ${a.criadoEm.slice(0, 16).replace("T", " ")}` },
  ];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <header>
        <h1 style={{ margin: 0 }}>Arquivo mensal da consignatária</h1>
        <p style={{ margin: "4px 0 0" }}>
          O arquivo em que a consignatária informa o valor do mês de cada associado. A averbadora envia em nome dela;
          a prévia mostra o que a confirmação faria, e só a confirmação muda o movimento.
        </p>
      </header>

      <Tabs
        tabs={[
          { key: "fila", label: "Aguardando confirmação", badge: pendentesQ.data?.arquivos.length || undefined },
          { key: "arquivos", label: "Arquivos por consignatária" },
          { key: "consignacoes", label: "Consignações" },
        ]}
        activeKey={aba}
        onChange={(k) => setAba(k as "fila" | "arquivos" | "consignacoes")}
      />

      {/* #281: incluir, alterar valor e encerrar uma a uma, sem o arquivo do mês. */}
      {aba === "consignacoes" ? <ConsignacoesDoCredor opcoesCredor={opcoesCredor} opcoesOrgao={opcoesOrgao} /> : null}

      {aba === "fila" ? (
      <Card>
        <h2 style={{ marginTop: 0 }}>Aguardando confirmação ({pendentesQ.data?.arquivos.length ?? 0})</h2>
        <p style={{ margin: "0 0 8px" }}>Arquivos enviados que ainda não foram confirmados. Clique para abrir a prévia.</p>
        {pendentesQ.isError && <p role="alert">{mensagemDeErro(pendentesQ.error, "Não foi possível carregar a fila.")}</p>}
        <DataTable
          caption="Arquivos aguardando confirmação"
          columns={colunasPendentes}
          rows={pendentesQ.data?.arquivos ?? []}
          rowKey={(a) => a.id}
          loading={pendentesQ.isLoading}
          onRowClick={abrirPendente}
          emptyState={<EstadoVazio titulo="Nenhum arquivo aguardando confirmação." />}
        />
      </Card>
      ) : null}

      {aba === "arquivos" ? (
      <>
      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <SelectField
            label="Consignatária"
            value={credorId === null ? "" : String(credorId)}
            onChange={(e) => { setCredorId(e.target.value ? Number(e.target.value) : null); setArquivoId(null); }}
            options={[{ value: "", label: "Escolha a consignatária" }, ...opcoesCredor]}
          />
          <SelectField
            label="Órgão/entidade"
            value={unidadeId === null ? "" : String(unidadeId)}
            onChange={(e) => { setUnidadeId(e.target.value ? Number(e.target.value) : null); setArquivoId(null); }}
            options={[{ value: "", label: "Escolha o órgão/entidade" }, ...opcoesOrgao]}
          />
          <TextField
            label="Competência (histórico)"
            placeholder="AAAAMM"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <label style={{ display: "grid", gap: 4 }}>
            <span>Arquivo (.txt ou .zip)</span>
            <input
              type="file"
              accept=".txt,.zip"
              aria-label="Arquivo da consignatária"
              disabled={credorId === null || unidadeId === null || enviar.isPending}
              onChange={aoEscolherArquivo}
            />
          </label>
        </div>
        {enviar.isPending && <p role="status">Lendo o arquivo…</p>}
        {enviar.isError && <p role="alert">{mensagemDeErro(enviar.error, "Não foi possível ler o arquivo.")}</p>}
      </Card>

      {aviso && <Card><p role="status" style={{ margin: 0 }}>{aviso}</p></Card>}

      {previa && arquivo && (
        <Card>
          <h2 style={{ marginTop: 0 }}>
            Prévia — {arquivo.nome} ({arquivo.competencia}) · {ESTADO_ROTULO[arquivo.estado]}
          </h2>
          {previa.substitui && (
            <p>Esta versão substitui a versão confirmada anterior da mesma competência: o que ela não trouxer deixa de valer.</p>
          )}
          {previa.movimentoDesatualizado && (
            <p role="note">
              O movimento desta competência já foi emitido. Confirmar muda o que a folha deveria receber: será preciso retificar o movimento.
            </p>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse" }} aria-label="Resumo por rubrica">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Rubrica</th>
                <th style={{ textAlign: "right" }}>Aceitas</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Mês anterior</th>
                <th style={{ textAlign: "right" }}>Saem por omissão</th>
              </tr>
            </thead>
            <tbody>
              {previa.rubricas.map((r) => (
                <tr key={r.rubrica}>
                  <td>{r.rubrica}</td>
                  <td style={{ textAlign: "right" }}>{r.aceitas}</td>
                  <td style={{ textAlign: "right" }}>{reais(r.totalCentavos)}</td>
                  <td style={{ textAlign: "right" }}>{reais(r.totalAnteriorCentavos)}</td>
                  <td style={{ textAlign: "right" }} title={r.encerramentos.map((x) => `FF ${x.ff} · ${x.matricula}`).join("\n")}>
                    {r.encerramentos.length}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {previa.bloqueios.length > 0 && (
            <section aria-label="Bloqueios">
              <h3>O que precisa de declaração antes de confirmar</h3>
              {previa.bloqueios.map((b) => {
                const k = chaveDoBloqueio(b);
                const d = digitado[k] ?? { justificativa: "", quantidade: "" };
                const mudar = (parcial: Partial<Declaracao>) => setDigitado((atual) => ({ ...atual, [k]: { ...d, ...parcial } }));
                return (
                  <div key={k} style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                    <p style={{ margin: 0 }}><strong>{b.motivo}</strong></p>
                    {b.tipo === "queda_grande" && (
                      <TextField
                        label={`Quantos encerramentos você confirma na rubrica ${b.rubrica}?`}
                        inputMode="numeric"
                        value={d.quantidade}
                        onChange={(e) => mudar({ quantidade: e.target.value.replace(/\D/g, "") })}
                      />
                    )}
                    {b.tipo !== "corte_passou" && (
                      <TextareaField
                        label={`Justificativa (rubrica ${b.rubrica})`}
                        value={d.justificativa}
                        onChange={(e) => mudar({ justificativa: e.target.value })}
                      />
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {previa.alertas.length > 0 && (
            <section aria-label="Alertas">
              <h3>Alertas (não impedem a confirmação)</h3>
              <ul>{previa.alertas.map((a, i) => <li key={`${a.tipo}-${a.matricula}-${a.rubrica}-${i}`}>{a.motivo}</li>)}</ul>
            </section>
          )}

          {confirmar.isError && <p role="alert">{mensagemDeErro(confirmar.error, "Não foi possível confirmar o arquivo.")}</p>}
          {arquivo.estado === "previa" && (
            <Button disabled={!podeConfirmar} onClick={() => { if (podeConfirmar) confirmar.mutate(previa); }}>
              {confirmar.isPending ? "Confirmando…" : "Confirmar arquivo"}
            </Button>
          )}
        </Card>
      )}

      {previa && (
        <Card>
          <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
            <SelectField
              label="Linhas"
              value={situacao}
              onChange={(e) => setSituacao(e.target.value as SituacaoLinhaArquivoConsignataria | "")}
              options={[{ value: "", label: "Todas" }, ...Object.entries(SITUACAO_ROTULO).map(([value, label]) => ({ value, label }))]}
            />
            <Button variant="ghost" onClick={() => void baixarCsv()}>Exportar CSV</Button>
          </div>
          <DataTable
            columns={colunasLinhas}
            rows={linhasQ.data?.linhas ?? []}
            rowKey={(l) => String(l.linha)}
            loading={linhasQ.isLoading}
            emptyState={<EstadoVazio titulo="Nenhuma linha nesta situação." />}
          />
        </Card>
      )}

      {confirmadoAberto && (
        <Card>
          <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <h2 style={{ margin: 0 }}>Resultado da folha — {confirmadoAberto.competencia}</h2>
            {resultadoQ.data && <Button variant="ghost" onClick={() => void baixarResultadoCsv()}>Exportar resultado</Button>}
          </div>
          {resultadoQ.isError ? (
            // So o retorno pendente e espera; qualquer outro erro aparece como erro.
            resultadoQ.error instanceof ApiHttpError && resultadoQ.error.code === "retorno_nao_importado" ? (
              <p>O resultado aparece depois que o retorno da competência for importado.</p>
            ) : (
              <p role="alert">{mensagemDeErro(resultadoQ.error, "Não foi possível ler o resultado.")}</p>
            )
          ) : (
            <DataTable
              caption="Resultado por linha"
              columns={colunasResultado}
              rows={resultadoQ.data?.resultado ?? []}
              rowKey={(l) => String(l.linha)}
              loading={resultadoQ.isLoading}
              emptyState={<EstadoVazio titulo="Nenhuma linha aceita neste arquivo." />}
            />
          )}
        </Card>
      )}

      {/* #279: a terceira coluna — o que o órgão repassou a este credor. */}
      {confirmadoAberto && credorId !== null && resultadoQ.data ? (
        <RepassesPainel
          titulo={`Conciliação — ${confirmadoAberto.competencia}`}
          totais={resultadoQ.data.totais}
          credores={opcoesCredor.filter((o) => o.value === String(credorId))}
          podeRegistrar
          porta={{
            chave: ["admin", "repasses", confirmadoAberto.unidadeId, confirmadoAberto.competencia, credorId],
            listar: () => atlas.admin.repasses.listar(confirmadoAberto.unidadeId, confirmadoAberto.competencia, credorId),
            registrar: (p) => atlas.admin.repasses.registrar(confirmadoAberto.unidadeId, confirmadoAberto.competencia, p),
            comprovanteUrl: (id) => atlas.admin.repasses.comprovanteUrl(confirmadoAberto.unidadeId, confirmadoAberto.competencia, id),
          }}
          aoRegistrar={() => void qc.invalidateQueries({ queryKey: ["admin", "consignatarias", credorId, "resultado"] })}
        />
      ) : null}

      {/* #280: o mesmo resultado, agrupado pelo credor final de cada rubrica. */}
      {confirmadoAberto && credorId !== null && resultadoQ.data ? (
        <RepassePorCredorFinal
          credorId={credorId}
          unidadeId={confirmadoAberto.unidadeId}
          competencia={confirmadoAberto.competencia}
          salvar={salvar}
        />
      ) : null}

      {credorId !== null && unidadeId !== null && (
        <Card>
          <h2 style={{ marginTop: 0 }}>Arquivos recebidos</h2>
          <DataTable
            columns={colunasArquivos}
            rows={arquivosQ.data?.arquivos ?? []}
            rowKey={(a) => a.id}
            loading={arquivosQ.isLoading}
            onRowClick={(a) => { setArquivoId(a.id); setDigitado({}); setAviso(null); }}
            emptyState={<EstadoVazio titulo="Nenhum arquivo desta consignatária para o órgão/entidade." />}
          />
        </Card>
      )}
      </>
      ) : null}
    </div>
  );
}
